import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { PublicPageShell } from '@/components/auth/PublicPageShell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  PasswordFields,
  usePasswordStrength,
} from '@/features/settings/components/PasswordFields';
import { DEFAULT_POST_LOGIN_PATH } from '@/lib/auth-redirect';
import AuthService, { AuthRequestError } from '@/services/AuthService';
import {
  ACCOUNT_LANGUAGES,
  ActivateFormValues,
  activateSchema,
  defaultLanguage,
} from '../account-access.schemas';

const TOKEN_HINT = 'Ask your account administrator to send a new invitation.';

/**
 * Landing page of the invitation / reactivation mail (`/activate?token=`).
 * The user picks a username and password; on success they are signed in.
 */
export function ActivatePage() {
  const navigate = useNavigate();
  const { token } = useSearch({ strict: false }) as { token?: string };
  const [error, setError] = useState<string | null>(null);

  const form = useForm<ActivateFormValues>({
    resolver: zodResolver(activateSchema),
    defaultValues: {
      loginname: '',
      password: '',
      repeatPassword: '',
      language: defaultLanguage(),
    },
  });
  const password = form.watch('password');
  const { strength, isStrongEnough } = usePasswordStrength(password);

  const onSubmit = async (values: ActivateFormValues) => {
    if (!token) return;
    setError(null);
    if (!(await isStrongEnough(values.password))) {
      form.setError('password', {
        message: 'Please choose a stronger password',
      });
      return;
    }
    try {
      await AuthService.getInstance().activate({
        token,
        loginname: values.loginname,
        password: values.password,
        language: values.language,
      });
    } catch (e) {
      if (e instanceof AuthRequestError) {
        if (/username/i.test(e.message)) {
          form.setError('loginname', { message: 'This username is taken' });
        } else if (/password/i.test(e.message)) {
          form.setError('password', {
            message: 'Please choose a stronger password',
          });
        } else if (/token/i.test(e.message)) {
          setError(
            `This activation link is invalid or has expired. ${TOKEN_HINT}`,
          );
        } else {
          setError(e.message);
        }
      } else {
        setError('Something went wrong. Please try again later.');
      }
      return;
    }
    navigate({ href: DEFAULT_POST_LOGIN_PATH, replace: true });
  };

  if (!token) {
    return (
      <PublicPageShell title="Activate account">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This activation link is incomplete. {TOKEN_HINT}
          </AlertDescription>
        </Alert>
      </PublicPageShell>
    );
  }

  const { isSubmitting } = form.formState;

  return (
    <PublicPageShell
      title="Activate account"
      description="Choose a username and password to finish setting up your account"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <FormField
            control={form.control}
            name="loginname"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username</FormLabel>
                <FormControl>
                  <Input
                    autoComplete="username"
                    autoCapitalize="none"
                    placeholder="john.doe"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Lowercase letters, digits, dots and underscores
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <PasswordFields
            form={form}
            strength={strength}
            label="Password"
            repeatLabel="Repeat password"
          />
          <FormField
            control={form.control}
            name="language"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Language</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {ACCOUNT_LANGUAGES.map((language) => (
                      <SelectItem key={language.value} value={language.value}>
                        {language.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>Used for emails we send you</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="animate-spin" />}
            Activate account
          </Button>
          <p className="text-muted-foreground text-center text-sm">
            Already activated?{' '}
            <Link to="/login" className="underline">
              Sign in
            </Link>
          </p>
        </form>
      </Form>
    </PublicPageShell>
  );
}
